import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Upload,
  Trash2,
  Copy,
  Check,
  X,
  ImageOff,
  Loader2,
  Filter,
  AlertCircle,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const resolveImageUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/")) return `${API_URL}${url}`;
  return url;
};

const formatBytes = (b) => {
  if (!b || b < 1024) return `${b || 0} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
};

const DATE_FILTERS = [
  { id: "all", label: "Tutte", days: null },
  { id: "week", label: "Ultima settimana", days: 7 },
  { id: "month", label: "Ultimo mese", days: 30 },
  { id: "3months", label: "Ultimi 3 mesi", days: 90 },
];

/**
 * Libreria media condivisa.
 *
 * Props:
 *  - token: string (admin token)
 *  - mode: "browse" | "picker"     default "browse"
 *  - multiSelect: bool             solo in picker mode
 *  - onPick: fn(urls[])           chiamata quando l'utente conferma la selezione
 *  - onClose: fn                  se in picker mode dentro un modal
 *  - excludeUrls: string[]        URL da non mostrare (es. gia' presenti nel post)
 */
const MediaLibrary = ({
  token,
  mode = "browse",
  multiSelect = true,
  onPick,
  onClose,
  excludeUrls = [],
}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [dateFilter, setDateFilter] = useState("all");
  const [selected, setSelected] = useState(new Set());
  const [copiedUrl, setCopiedUrl] = useState("");
  const [preview, setPreview] = useState(null); // {url, name, size, modified}
  const [deleteError, setDeleteError] = useState("");
  const fileInputRef = useRef(null);

  const excludeSet = new Set(excludeUrls);

  const reload = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      const f = DATE_FILTERS.find((d) => d.id === dateFilter);
      if (f?.days) {
        const since = new Date(Date.now() - f.days * 86400_000);
        params.set("since", since.toISOString().slice(0, 10));
      }
      const res = await fetch(
        `${API_URL}/api/admin/media${params.toString() ? "?" + params : ""}`,
        { headers: { "X-Admin-Token": token } }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setItems(Array.isArray(json?.data) ? json.data : []);
    } catch (e) {
      setError("Impossibile caricare la libreria.");
    } finally {
      setLoading(false);
    }
  }, [token, dateFilter]);

  useEffect(() => {
    if (token) reload();
  }, [reload, token]);

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) =>
      f.type.startsWith("image/")
    );
    if (files.length === 0) return;
    setUploading(true);
    setError("");
    try {
      for (const f of files) {
        if (f.size > 8 * 1024 * 1024) {
          throw new Error(`"${f.name}": file troppo grande (max 8 MB).`);
        }
        const data = new FormData();
        data.append("file", f);
        const res = await fetch(`${API_URL}/api/admin/upload`, {
          method: "POST",
          headers: { "X-Admin-Token": token },
          body: data,
        });
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          throw new Error(d?.detail || `Upload di "${f.name}" fallito.`);
        }
      }
      await reload();
    } catch (e) {
      setError(e.message || "Errore upload.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    uploadFiles(e.dataTransfer?.files);
  };
  const handleDragOver = (e) => e.preventDefault();

  const toggleSelect = (url) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (multiSelect) {
        next.has(url) ? next.delete(url) : next.add(url);
      } else {
        next.clear();
        next.add(url);
      }
      return next;
    });
  };

  const copyUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedUrl(url);
      setTimeout(() => setCopiedUrl(""), 1500);
    } catch {
      /* clipboard may be unavailable */
    }
  };

  const deleteItem = async (item) => {
    if (!window.confirm(`Eliminare definitivamente "${item.name}"?`)) return;
    setDeleteError("");
    try {
      const res = await fetch(
        `${API_URL}/api/admin/media?url=${encodeURIComponent(item.url)}`,
        {
          method: "DELETE",
          headers: { "X-Admin-Token": token },
        }
      );
      const json = await res.json().catch(() => ({}));
      if (res.status === 409) {
        setDeleteError(
          `Immagine usata in ${json.posts_count || 0} post e ${
            json.projects_count || 0
          } progetti. Rimuovila prima da lì.`
        );
        return;
      }
      if (!res.ok) {
        setDeleteError(json?.detail || `Errore ${res.status}`);
        return;
      }
      setPreview(null);
      setSelected((prev) => {
        const n = new Set(prev);
        n.delete(item.url);
        return n;
      });
      await reload();
    } catch {
      setDeleteError("Errore di rete.");
    }
  };

  const confirmPick = () => {
    onPick?.(Array.from(selected));
    onClose?.();
  };

  const visibleItems = items.filter((i) => !excludeSet.has(i.url));

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
      {/* ============= Toolbar ============= */}
      <div className="flex flex-wrap items-center gap-3 p-4 border-b border-gray-100">
        <label className="inline-flex items-center gap-2 cursor-pointer bg-[#0A1F44] hover:bg-[#0F2A5C] text-white rounded-lg h-10 px-4 text-sm font-montserrat font-semibold transition-colors disabled:opacity-60">
          {uploading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Upload className="w-4 h-4" />
          )}
          {uploading ? "Caricamento…" : "Carica immagini"}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => uploadFiles(e.target.files)}
            disabled={uploading}
          />
        </label>

        <div className="inline-flex items-center gap-1 bg-gray-100 rounded-lg p-1">
          <Filter className="w-3.5 h-3.5 text-gray-500 ml-2" />
          {DATE_FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setDateFilter(f.id)}
              className={`h-8 px-3 rounded-md text-xs font-montserrat font-semibold transition-colors ${
                dateFilter === f.id
                  ? "bg-white text-[#0A1F44] shadow-sm"
                  : "text-gray-600 hover:text-[#0A1F44]"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="text-xs text-gray-400 ml-auto">
          {visibleItems.length} {visibleItems.length === 1 ? "immagine" : "immagini"}
        </div>

        {mode === "picker" && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="h-10 px-4 rounded-lg text-sm font-montserrat font-semibold text-gray-600 hover:text-[#0A1F44] transition-colors"
            >
              Annulla
            </button>
            <button
              type="button"
              onClick={confirmPick}
              disabled={selected.size === 0}
              className="h-10 px-5 rounded-lg text-sm font-montserrat font-bold bg-[#F4C542] hover:bg-[#e6b838] text-[#0A1F44] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Aggiungi {selected.size > 0 ? `(${selected.size})` : ""}
            </button>
          </div>
        )}
      </div>

      {/* ============= Errori ============= */}
      {error && (
        <div className="mx-4 mt-4 flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          {error}
        </div>
      )}

      {/* ============= Dropzone / grid ============= */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        className="p-4 min-h-[280px]"
      >
        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-400">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : visibleItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center text-gray-400">
            <ImageOff className="w-10 h-10 mb-3" />
            <p className="text-sm font-montserrat font-semibold text-gray-500 mb-1">
              Nessuna immagine
            </p>
            <p className="text-xs">
              Trascina qui i file o usa il bottone "Carica immagini".
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {visibleItems.map((item) => {
              const isSel = selected.has(item.url);
              const isPickerMode = mode === "picker";
              return (
                <div
                  key={item.url}
                  className={`group relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                    isSel
                      ? "border-[#F4C542] ring-2 ring-[#F4C542]/30"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <img
                    src={resolveImageUrl(item.url)}
                    alt={item.name}
                    loading="lazy"
                    className="w-full h-full object-cover cursor-pointer"
                    onClick={() =>
                      isPickerMode ? toggleSelect(item.url) : setPreview(item)
                    }
                  />

                  {/* Overlay info */}
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <p className="text-white text-[10px] font-montserrat font-semibold truncate">
                      {formatDate(item.modified)}
                    </p>
                    <p className="text-white/80 text-[10px]">
                      {formatBytes(item.size)}
                    </p>
                  </div>

                  {/* Checkmark selezione (picker) */}
                  {isPickerMode && isSel && (
                    <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[#F4C542] text-[#0A1F44] flex items-center justify-center shadow-md">
                      <Check className="w-4 h-4" strokeWidth={3} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ============= Preview modal (solo in browse mode) ============= */}
      {preview && (
        <div
          className="fixed inset-0 z-[9998] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setPreview(null)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="bg-white rounded-2xl overflow-hidden shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <div className="min-w-0">
                <p className="font-montserrat font-bold text-[#0A1F44] truncate">
                  {preview.name}
                </p>
                <p className="text-xs text-gray-500">
                  {formatBytes(preview.size)} · {formatDate(preview.modified)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setPreview(null)}
                className="w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-500 hover:text-[#0A1F44]"
                aria-label="Chiudi preview"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 bg-gray-950 flex items-center justify-center overflow-hidden">
              <img
                src={resolveImageUrl(preview.url)}
                alt={preview.name}
                className="max-w-full max-h-[70vh] object-contain"
              />
            </div>
            {deleteError && (
              <div className="px-4 pt-3 flex items-start gap-2 text-sm text-red-700">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{deleteError}</span>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2 p-4 border-t border-gray-100 bg-gray-50">
              <code className="flex-1 min-w-0 text-xs font-mono bg-white border border-gray-200 rounded px-3 py-2 truncate">
                {preview.url}
              </code>
              <button
                type="button"
                onClick={() => copyUrl(preview.url)}
                className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg text-sm font-montserrat font-semibold bg-white border border-gray-200 hover:border-[#0A1F44] text-[#0A1F44] transition-colors"
              >
                {copiedUrl === preview.url ? (
                  <>
                    <Check className="w-4 h-4 text-green-600" /> Copiato
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" /> Copia URL
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => deleteItem(preview)}
                className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg text-sm font-montserrat font-semibold bg-red-50 hover:bg-red-100 text-red-700 transition-colors"
              >
                <Trash2 className="w-4 h-4" /> Elimina
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaLibrary;
